// components/repoRespondent.tsx
import React, { useEffect, useState } from "react";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";
import { fetchAppointmentReasons } from "services/assetRecoveryService";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import { Asset, AssetAppeal, AssetAppealRequestActionStatusEnum } from "types";
import "../../../pages/AssetRecovery/AssetRecoveryRegistration/AssetReferralRegisterCss.css";
import {
  addAppealReport,
  getAssetAppealById,
  getRespondentAssetAppealById,
} from "services/assetAppealService";
interface DailyReportProps {
  appealId?: string;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  onNewDataAdded?: () => void;
  appeal: AssetAppeal | null;
  assets: Asset[] | null;
  setAppeal?: React.Dispatch<React.SetStateAction<AssetAppeal | null>>;
}

export const RespondentAppealDailyReport: React.FC<DailyReportProps> = ({
  appealId,
  setActiveTab,
  onNewDataAdded,
  appeal,
  assets,
  setAppeal,
}) => {
  const [formData, setFormData] = useState<{
    court_hearing_date: string;
    daily_report_summary: string;
    daily_report_summary_file: string;
    court_order_for_next_appointment: string;
    next_appointment_reasons: string;
    next_appointment_date: string;
    assets: string[];
  }>({
    court_hearing_date: "",
    daily_report_summary: "",
    daily_report_summary_file: "",
    court_order_for_next_appointment: "",
    next_appointment_reasons: "",
    next_appointment_date: "",
    assets: [],
  });

  const [submitting, setSubmitting] = useState(false);
  const [reasons, setReasons] = useState<{ id: string; name: string }[]>([]);
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();

  const handleInputChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | { target: { name: string; value: string | string[] | boolean } },
  ) => {
    const { name, value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const handleAddDailyReport = async () => {
    // Validate required fields
    if (!formData.daily_report_summary.trim()) {
      showTooltip(t("daily_report_summary_required"), "error");
      return;
    }

    if (!formData.court_hearing_date) {
      showTooltip(t("court_hearing_date_required"), "error");
      return;
    }

    const data = new FormData();
    if (appeal?.appeal_type === "appeal_by_respondant") {
      data.append("asset_respondent_appeal", appealId as string);
    } else data.append("asset_appeal_request", appealId as string);

    data.append("court_hearing_date", formData.court_hearing_date);
    data.append("daily_report_summary", formData.daily_report_summary);
    data.append(
      "court_order_for_next_appointment",
      formData.court_order_for_next_appointment || "",
    );
    data.append(
      "next_appointment_reasons",
      formData.next_appointment_reasons || "",
    );
    data.append("next_appointment_date", formData.next_appointment_date || "");
    //  if (formData.assets.length > 0) {
    //   formData.assets.forEach((asset) => {
    //     data.append("asset", asset);
    //   });
    // }
    if (uploadedFiles.length > 0) {
      uploadedFiles.forEach((file) => {
        data.append("daily_report_summary_documents", file);
      });
    }

    try {
      setSubmitting(true);
      let response;
      if (appeal?.appeal_type === "appeal_by_respondant") {
        if (
          appeal?.action_status ===
          AssetAppealRequestActionStatusEnum?.APPEAL_APPROVED
        ) {
          response = await addAppealReport(data);
        }
      } else {
        if (
          appeal?.action_status ===
          AssetAppealRequestActionStatusEnum?.APPEAL_ON_LITIGATION
        ) {
          response = await addAppealReport(data);
        }
      }
      if (response) {
        showTooltip(t("daily_report_submitted_successfully"), "success");
        onNewDataAdded?.();
        setFormData({
          court_hearing_date: "",
          daily_report_summary: "",
          daily_report_summary_file: "",
          court_order_for_next_appointment: "",
          next_appointment_reasons: "",
          next_appointment_date: "",
          assets: [],
        });
        setUploadedFiles([]);
      } else {
        showTooltip(t("unable_to_add_report"), "error");
      }

      if (setAppeal && response) {
        let data;
        if (appeal?.appeal_type === "appeal_by_respondant") {
          data = await getRespondentAssetAppealById(appealId as string);
        } else {
          data = await getAssetAppealById(appealId as string);
        }
        if (data) {
          setAppeal(data);
          setActiveTab?.("Report");
        }
      }
    } catch (err) {
      console.error("Error submitting daily report:", err);
      showTooltip(t("daily_report_not_submitted_successfully"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    fetchAppointmentReasons().then(setReasons).catch(console.error);
  }, []);

  return (
    <div className="p-6 ">
      <p className="text-sm  mb-2 text-gray-400">Fill Daily Report</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleAddDailyReport();
        }}
      >
        <div className="flex flex-col gap-2">
          {/* Daily Report Summary */}
          <div className="md:col-span-2 flex flex-col">
            <label className="referral-popup-label">Report Summary</label>
            <textarea
              className="referral-form-field min-h-[100px] w-full"
              onChange={handleInputChange}
              name="daily_report_summary"
              placeholder="daily report detail here..."
              value={formData.daily_report_summary || ""}
            />
          </div>

          {/* Report Document Upload */}
          <div className="md:col-span-2">
            <MultipleFileUploadComponent
              onFilesChange={setUploadedFiles}
              uploadedFiles={uploadedFiles}
              label="Report Doc"
            />
          </div>

          {/* Appointment Date */}
          <div className="flex flex-col">
            <label className="referral-popup-label">Date</label>
            <input
              type="date"
              className="referral-form-field"
              onChange={handleInputChange}
              name="court_hearing_date"
              value={formData.court_hearing_date || ""}
            />
          </div>
          <div className="flex flex-col">
            <label className="referral-popup-label">
              Next Appointment Date
            </label>
            <input
              type="date"
              className="referral-form-field"
              onChange={handleInputChange}
              name="next_appointment_date"
              value={formData.next_appointment_date || ""}
            />
          </div>

          {/* Appointment Reason */}
          <div className="md:col-span-2">
            <label className="referral-popup-label">Appointment Reason</label>
            <select
              className="referral-form-field"
              onChange={handleInputChange}
              name="next_appointment_reasons"
              value={formData.next_appointment_reasons || ""}
            >
              <option value="" disabled>
                Select Appointment Reason
              </option>
              {reasons?.map((reason) => (
                <option value={reason?.name} key={reason?.id}>
                  {reason.name}
                </option>
              ))}
            </select>
          </div>

          {/* Court Order for Next Appointment */}
          <div className="md:col-span-2 flex flex-col">
            <label className="referral-popup-label">
              Court Order for Next Appointment
            </label>
            <textarea
              className="referral-form-field min-h-[80px]"
              onChange={handleInputChange}
              name="court_order_for_next_appointment"
              placeholder="Enter court order details..."
              value={formData.court_order_for_next_appointment || ""}
            />
          </div>
        </div>

        <div className="flex justify-end mt-6 ">
          <button
            type="submit"
            className="referral-form-button text-white px-8 py-3"
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Set Appointment"}
          </button>
        </div>
      </form>
    </div>
  );
};
